import 'react-datepicker/dist/react-datepicker.css';
import { COLORS, TYPOGRAPHY } from '@app/constants';
import format from 'date-fns/format';
import getDate from 'date-fns/getDate';
import { ru } from 'date-fns/locale';
import { FC, useMemo } from 'react';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import styled from 'styled-components';
import { Header } from './components';
import { getTitleByDate } from './helpers';

export interface StyledDatePickerProps extends ReactDatePickerProps {

  className?: string;
  doneSessions?: Date[];
  futureSessions?: Date[];
  isInline?: boolean;
  pastYear?: number;
  isPastYears?: boolean;
  numberOfYearsAgo?: number;
  onUpdateDate?: (date: Date) => void;
  hasResetButton?: boolean;
  onReset?: () => void;

}

const StyledDatePicker: FC<StyledDatePickerProps> = ({

  className,
  pastYear = new Date().getFullYear(),
  isPastYears = false,
  doneSessions = [],
  futureSessions = [],
  isInline = false,
  numberOfYearsAgo = 100,
  onUpdateDate,
  hasResetButton = false,
  onReset,
  ...props

}) => {

  const renderDayContents = (day: number, date: Date) => {

    const title = getTitleByDate(date);

    for (const dateSession of doneSessions) {

      const tooltipText = `${title}: ${format(date, 'PP', {

        locale: ru,

      })}`;

      if (getDate(date) === getDate(dateSession)) {

        return <span title = { tooltipText }>{ getDate(date) }</span>;

      }

    }

    for (const dateSession of futureSessions) {

      const tooltipText = `${title}: ${format(date, 'PP', {

        locale: ru,

      })}`;

      if ( getDate(date) === getDate(dateSession) ) {

        return <span title = { tooltipText } style = {{ overflow: 'hidden' }}>{ getDate(date) }</span>;

      }

    }

    return getDate(date);

  };

  const highlightesDoneSessions = [

    {

      'react-datepicker__day--session react-datepicker__day--past-session':
      doneSessions,

    },

  ];

  const highlightesFutureSessions = [

    {

      'react-datepicker__day--session': futureSessions,

    },

  ];

  const finishedHighlightes = [

    ...highlightesDoneSessions,
    ...highlightesFutureSessions,

  ];

  const { handleIncreaseMonth, handleDecreaseMonth, handleChangeYear } =

    useMemo(() => {

      const update = ( date: Date ) => {

        onUpdateDate && onUpdateDate(date);

      };

      const handleIncreaseMonth = ( date: Date ) => {

        const nextDate = new Date(date);
        nextDate.setMonth(nextDate.getMonth() + 1);
        nextDate.setDate(1);
        update(nextDate);

      };

      const handleDecreaseMonth = ( date: Date ) => {

        const nextDate = new Date(date);
        nextDate.setMonth(nextDate.getMonth() - 1);
        nextDate.setDate(1);
        update(nextDate);

      };

      const handleChangeYear = ( date: Date, year: number ) => {

        const nextDate = new Date(date);
        nextDate.setFullYear(year);
        nextDate.setDate(1);
        update(nextDate);

      };

      return { handleIncreaseMonth, handleDecreaseMonth, handleChangeYear };

    }, [ onUpdateDate ]);

  return (

    <Root className = { className }>

      <DatePicker

        inline = { isInline }
        showPopperArrow = { false }
        calendarClassName = "custom-datepicker"
        renderCustomHeader = { ({
          decreaseMonth,
          increaseMonth,
          changeYear,
          ...params

        }) => (

          <Header

            hasResetButton = { hasResetButton }
            isPastYears = { isPastYears }
            pastYear = { pastYear }
            numberOfYearsAgo = { numberOfYearsAgo }
            onReset = { onReset }
            changeYear = { (year: number) => {
              handleChangeYear(params.date, year);
              changeYear(year);

            } }

            increaseMonth = { () => {

              handleIncreaseMonth(params.date);
              increaseMonth();

            } }

            decreaseMonth = { () => {

              handleDecreaseMonth(params.date);
              decreaseMonth();

            } }

            { ...params }

          />

        ) }

        renderDayContents = { renderDayContents }
        highlightDates = { finishedHighlightes }
        dateFormat = "dd.MM.yyyy"
        { ...props }

      />

    </Root>

  );

};

const Root = styled.div`
  .custom-datepicker {
    border: 0;
    font-family: inherit;
    color: ${COLORS.grayscale600};
    border-radius: 10px;
    overflow: hidden;
    background-color: transparent;

    .react-datepicker__header {
      border: 0;
      margin-bottom: 16px;
      padding-bottom: 0;
      background-color: transparent;
    }

    .react-datepicker__day-names {
      margin-bottom: 0;
    }

    .react-datepicker__day-name,
    .react-datepicker__day {
      margin-left: 10px;
      margin-right: 10px;
      overflow: hidden;
      &:first-child {
        margin-left: 0;
      }
      &:last-child {
        margin-right: 0;
      }
    }

    .react-datepicker__day-name {
      ${TYPOGRAPHY.footnoteRegular13};
      color: #212731;
      text-transform: capitalize;
      width: 28px;
    }

    .react-datepicker__month {
      margin: 4px;
    }

    .react-datepicker__day {
      ${TYPOGRAPHY.footnoteMedium13};
      width: 28px;
      height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0;
      margin-top: 0;
      border-radius: 8px;
      color: ${COLORS.grayscale600};

      &--keyboard-selected,
      &:hover {
        background-color: ${COLORS.grayscale100};
      }

      &--outside-month,
      &--disabled {
        color: #C4CBD4;
        font-size: 14px;
        font-weight: 500;
        line-height: 20;
      }

      &--today {
        background-color: ${COLORS.secondary50};
        color: #212731;
        &:hover {
          background-color: ${COLORS.secondary100};
        }
        &::before {
          background-color: ${COLORS.black};
        }
      }

      &--session {
        position: relative;
        color: #212731;
        border: 1px solid ${COLORS.secondary300};

        &::before {
          background-color: ${COLORS.secondary300};
        }
      }

      &--past-session {
        color: ${COLORS.secondary500};

        &::before {
          background-color: ${COLORS.secondary500};
        }
      }

      &--selected {
        background-color: ${COLORS.secondary300};
        color: ${COLORS.black};

        &:hover {
          background-color: ${COLORS.secondary300};
        }
        &::before {
          background-color: ${COLORS.black};
        }
      }
    }

    .react-datepicker__week {
      margin-bottom: 4px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

export default StyledDatePicker;
